import { useEffect, useMemo, useState } from 'react'
import { AlertCircle } from 'lucide-react'
import { useTranslation } from '@kostbar/i18n'
import {
  useRolePermissions,
  useUpdateRolePermissions,
} from '../hooks/use-role-permissions'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useAuth } from '@/features/auth/hooks/use-auth'
import { usePermissions } from '@/shared/hooks/use-permissions'
import { ACTIONS } from '@/shared/constants/actions'
import { ROLES } from '@/shared/constants/roles'

/**
 * `report:sales:view` -> `report / sales view`.
 *
 * Everything after the group is kept: three-part actions (edge:pairing:create,
 * restaurant:scheduling:manage) used to lose their verb here and render as "edge / pairing", which
 * reads like a permission to pair rather than to create a pairing.
 */
function formatActionLabel(action: string, named?: string) {
  const [group, ...rest] = action.split(':')
  const groupLabel = group.replace('-', ' ')
  const actionLabel = named ?? rest.join(' ').replace(/[-_]/g, ' ')
  return actionLabel ? `${groupLabel} / ${actionLabel}` : groupLabel
}

/**
 * Actions whose derived label would not read as German: "order proposal order" says nothing to a
 * manager who knows the report as "Bestellvorschlag" (CHG-2026-10-bestellvorschlag).
 */
const NAMED_ACTIONS: Partial<
  Record<
    string,
    | 'actionLabels.orderProposalView'
    | 'actionLabels.orderProposalOrder'
    | 'actionLabels.purchaseOrderView'
    | 'actionLabels.purchaseOrderOrder'
  >
> = {
  [ACTIONS.REPORT_ORDER_PROPOSAL_VIEW]: 'actionLabels.orderProposalView',
  [ACTIONS.REPORT_ORDER_PROPOSAL_ORDER]: 'actionLabels.orderProposalOrder',
  [ACTIONS.PURCHASE_ORDER_VIEW]: 'actionLabels.purchaseOrderView',
  [ACTIONS.PURCHASE_ORDER_ORDER]: 'actionLabels.purchaseOrderOrder',
}

export function RolesPage() {
  const { user, hasRole } = useAuth()
  const { can } = usePermissions()
  const { t: tRoles } = useTranslation('roles')
  const { t: tCommon } = useTranslation('common')
  const labelOf = (action: string) => {
    const named = NAMED_ACTIONS[action]
    return formatActionLabel(action, named ? tRoles(named) : undefined)
  }
  const [tenantIdInput, setTenantIdInput] = useState(
    user?.tenantId?.toString() ?? '',
  )

  const tenantId = useMemo(() => {
    if (user?.tenantId) return user.tenantId
    const parsed = Number(tenantIdInput)
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null
  }, [user?.tenantId, tenantIdInput])

  const { data, isLoading, error } = useRolePermissions({
    tenantId,
    enabled: Boolean(tenantId),
  })
  const updatePermissions = useUpdateRolePermissions(tenantId)

  const [selectedRole, setSelectedRole] = useState<string>('')
  const [selectedActions, setSelectedActions] = useState<Array<string>>([])

  const roles = data?.roles || []
  const actions = data?.actions || []

  useEffect(() => {
    if (!roles.length) return
    const initial = roles[0]?.role
    if (!selectedRole && initial) {
      setSelectedRole(initial)
      setSelectedActions(roles[0].actions)
    }
  }, [roles, selectedRole])

  useEffect(() => {
    const role = roles.find((item) => item.role === selectedRole)
    if (role) setSelectedActions(role.actions)
  }, [roles, selectedRole])

  const currentRole = roles.find((item) => item.role === selectedRole)
  const isLocked = currentRole?.locked

  const groupedActions = useMemo(() => {
    const grouped: Record<string, Array<string> | undefined> = {}
    actions.forEach((action) => {
      const [group] = action.split(':')
      const key = group || 'general'
      if (!grouped[key]) grouped[key] = []
      grouped[key].push(action)
    })
    return grouped
  }, [actions])

  function toggleAction(action: string, checked: boolean) {
    setSelectedActions((prev) => {
      const next = new Set(prev)
      if (checked) next.add(action)
      else next.delete(action)
      return Array.from(next)
    })
  }

  function handleSave() {
    if (!selectedRole || selectedActions.length === 0) return
    updatePermissions.mutate({
      role: selectedRole,
      data: { actions: selectedActions },
    })
  }

  const canManage = can(ACTIONS.ROLE_PERMISSION_MANAGE)
  const canAssignSuperAdmin = hasRole(ROLES.SUPER_ADMIN)

  if (!canManage) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>{tRoles('permissions.deniedTitle')}</AlertTitle>
        <AlertDescription>
          {tRoles('permissions.deniedDescription')}
        </AlertDescription>
      </Alert>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">{tRoles('title')}</h1>
        <p className="text-muted-foreground mt-1">{tRoles('subtitle')}</p>
      </div>

      {canAssignSuperAdmin && !user?.tenantId && (
        <div className="max-w-xs">
          <Label htmlFor="tenant-id">{tRoles('filters.tenantIdLabel')}</Label>
          <Input
            id="tenant-id"
            type="number"
            value={tenantIdInput}
            onChange={(event) => setTenantIdInput(event.target.value)}
            placeholder={tRoles('filters.tenantIdPlaceholder')}
            className="mt-1.5"
          />
        </div>
      )}

      {!tenantId && canAssignSuperAdmin && !user?.tenantId && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>{tRoles('tenant.requiredTitle')}</AlertTitle>
          <AlertDescription>
            {tRoles('tenant.requiredDescription')}
          </AlertDescription>
        </Alert>
      )}

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>{tCommon('error')}</AlertTitle>
          <AlertDescription>
            {tRoles('status.errorDescription')}
          </AlertDescription>
        </Alert>
      )}

      {isLoading ? (
        <div className="text-sm text-muted-foreground">
          {tRoles('status.loading')}
        </div>
      ) : (
        <Card>
          <CardContent className="pt-6 space-y-6">
            <div className="max-w-xs">
              <Label htmlFor="role-select">{tRoles('filters.roleLabel')}</Label>
              <Select value={selectedRole} onValueChange={setSelectedRole}>
                <SelectTrigger id="role-select" className="mt-1.5">
                  <SelectValue
                    placeholder={tRoles('filters.rolePlaceholder')}
                  />
                </SelectTrigger>
                <SelectContent>
                  {roles.map((role) => (
                    <SelectItem key={role.role} value={role.role}>
                      {role.role.replace('_', ' ')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {isLocked && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>{tRoles('edit.notEditableTitle')}</AlertTitle>
                <AlertDescription>
                  {tRoles('edit.notEditableDescription')}
                </AlertDescription>
              </Alert>
            )}

            {Object.entries(groupedActions).map(([group, groupActions]) => (
              <div key={group} className="space-y-2">
                <div className="text-sm font-medium capitalize">
                  {group.replace('-', ' ')}
                </div>
                <div className="grid gap-2 md:grid-cols-2">
                  {groupActions?.map((action) => (
                    <label
                      key={action}
                      className="flex items-center gap-2 text-sm"
                    >
                      <Checkbox
                        checked={selectedActions.includes(action)}
                        onCheckedChange={(checked) =>
                          toggleAction(action, Boolean(checked))
                        }
                        disabled={isLocked}
                      />
                      {labelOf(action)}
                    </label>
                  ))}
                </div>
              </div>
            ))}

            <Button
              onClick={handleSave}
              disabled={
                updatePermissions.isPending ||
                isLocked ||
                selectedActions.length === 0
              }
            >
              {updatePermissions.isPending
                ? tCommon('saving')
                : tRoles('actions.save')}
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
